import type { ExportService, TurtleResult } from './ExportService';
import type { Vocabulary } from '@semantical/modules/vocabulary';
import type { KnowledgeGraph } from '@semantical/modules/knowledge-graph';
import type Diagram from '@semantical/modules/diagram/Diagram';
import type { PrefixMap } from '@semantical/modules/ontology-model';
import type { Domain } from '@semantical/modules/semantical-core';
import type { OntologyEnabledBoundedContext } from '@semantical/modules/workspace';
import { downloadDomainExport, downloadDomainExportAsTurtle } from '@semantical/modules/workspace';

/**
 * Implementation of ExportService for Turtle and JSON export functionality.
 */
export class ExportServiceImpl implements ExportService {
    async generateVocabularyTurtle(vocabulary: Vocabulary): Promise<TurtleResult> {
        try {
            const content = await vocabulary.toTurtle();
            return { success: true, content };
        } catch (e) {
            console.error('Failed to generate Vocabulary Turtle', e);
            return { success: false, error: String(e) };
        }
    }

    async generateKnowledgeGraphTurtle(
        knowledgeGraph: KnowledgeGraph,
        domainPrefixes: PrefixMap
    ): Promise<TurtleResult> {
        try {
            const content = await knowledgeGraph.toTurtle(domainPrefixes);
            return { success: true, content };
        } catch (e) {
            console.error('Failed to generate KnowledgeGraph Turtle', e);
            return { success: false, error: String(e) };
        }
    }

    generateDiagramTurtle(
        diagram: Diagram,
        knowledgeGraph: KnowledgeGraph,
        domainPrefixes: PrefixMap
    ): TurtleResult {
        try {
            const content = diagram.toTurtle(knowledgeGraph, domainPrefixes);
            return { success: true, content };
        } catch (e) {
            console.error('Failed to generate Diagram Turtle', e);
            return { success: false, error: String(e) };
        }
    }

    downloadTurtle(content: string, filename: string = 'vocabulary.ttl'): void {
        const blob = new Blob([content], { type: 'text/turtle;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    exportToJson(
        rootDomains: Domain[],
        boundedContext?: OntologyEnabledBoundedContext
    ): void {
        downloadDomainExport(rootDomains, boundedContext, {
            includeDatasets: true,
            includeDiagrams: true,
            prettyPrint: true
        });
    }

    async exportToTurtle(
        rootDomains: Domain[],
        boundedContext?: OntologyEnabledBoundedContext
    ): Promise<void> {
        try {
            await downloadDomainExportAsTurtle(rootDomains, boundedContext, {
                includeDatasets: true,
                includeDiagrams: true
            });
        } catch (error) {
            console.error('Failed to export to Turtle:', error);
        }
    }
}

/**
 * Factory function to create an ExportService instance.
 */
export function createExportService(): ExportService {
    return new ExportServiceImpl();
}
